import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Calendar, CreditCard, Users, Building, Clock, CheckCircle, CheckCircle2 } from 'lucide-react';
import { toast } from './ui/sonner';

export const BookingModal = ({ children, selectedPlan = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Schritt 1: Abo-Auswahl
    selectedPlan: selectedPlan || '',
    addTimeTracking: false,
    addIDA: false,
    
    // Schritt 2: Unternehmensdaten
    companyName: '',
    industry: '',
    employees: '',
    locations: '',
    
    // Schritt 3: Kontaktdaten
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    
    // Schritt 4: Rechnungsdaten
    billingCompany: '',
    billingAddress: '',
    billingCity: '',
    billingZip: '',
    billingCountry: 'Deutschland',
    vatNumber: '',
    
    // Schritt 5: Onboarding
    onboardingDate: '',
    onboardingTime: '',
    onboardingType: 'online',
    specialRequirements: '',
    
    // Schritt 6: Zahlung
    paymentMethod: 'stripe',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cardName: '',
    
    // Rechtliches
    acceptTerms: false,
    acceptPrivacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const planOptions = [
    { value: 'ida-monthly', label: 'IDA Monats Abo', price: '€34/Monat (zzgl. MwSt.)' },
    { value: 'ida-yearly', label: 'IDA Jahres Abo', price: '€340/Jahr (zzgl. MwSt., 2 Monate kostenlos)' },
    { value: 'timetracking-monthly', label: 'Zeiterfassung Monats Abo', price: '€39/Monat (zzgl. MwSt.)' },
    { value: 'timetracking-yearly', label: 'Zeiterfassung Jahres Abo', price: '€390/Jahr (zzgl. MwSt.)' },
    { value: 'enterprise', label: 'Für Veranstalter', price: 'Individuell' }
  ];

  const industries = [
    'Spielhalle', 'Wettbüro', 'Gastronomie mit Geldspielgeräten',
    'Veranstaltungen', 'Casino', 'Sonstige'
  ];

  const timeSlots = [
    '09:00 - 10:00', '10:00 - 11:00', '11:00 - 12:00',
    '13:00 - 14:00', '14:00 - 15:00', '15:00 - 16:00', '16:00 - 17:00'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nextStep = () => {
    // For enterprise (Veranstalter) plan, limit to 3 steps
    // Payment step (6) is disabled - orders sent via email
    const maxSteps = formData.selectedPlan === 'enterprise' ? 3 : 5;
    if (currentStep < maxSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const calculateTotal = () => {
    if (formData.selectedPlan === 'enterprise') return 0; // No fixed price for enterprise
    
    let total = 0;
    
    // Hauptabo
    if (formData.selectedPlan === 'ida-monthly') total = 34;
    if (formData.selectedPlan === 'ida-yearly') total = 340;
    if (formData.selectedPlan === 'timetracking-monthly') total = 39;
    if (formData.selectedPlan === 'timetracking-yearly') total = 390;
    
    // Zusätzliche Module
    if (formData.addTimeTracking === 'monthly') total += 39;
    if (formData.addTimeTracking === 'yearly') total += 390;
    if (formData.addIDA === 'monthly') total += 34;
    if (formData.addIDA === 'yearly') total += 340;
    
    return total;
  };

  /* PAYMENT PROCESSING DISABLED - Orders sent via email instead
  const processPayment = async () => {
    setIsSubmitting(true);

    try {
      const originUrl = window.location.origin;

      if (formData.paymentMethod === 'stripe') {
        // Stripe Integration
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            package_id: formData.selectedPlan,
            amount: calculateTotal(),
            currency: 'eur',
            origin_url: originUrl,
            metadata: {
              customer_email: formData.email,
              customer_name: `${formData.firstName} ${formData.lastName}`,
              company: formData.companyName,
              subscription_type: formData.selectedPlan
            }
          })
        });

        if (!response.ok) throw new Error('Payment session creation failed');

        const { url } = await response.json();
        window.location.href = url; // Redirect to Stripe Checkout

      } else if (formData.paymentMethod === 'paypal') {
        // PayPal Integration
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/paypal/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: calculateTotal(),
            currency: 'EUR',
            return_url: `${originUrl}/payment/success`,
            cancel_url: `${originUrl}/payment/cancel`,
            metadata: {
              customer_email: formData.email,
              subscription_type: formData.selectedPlan
            }
          })
        });

        if (!response.ok) throw new Error('PayPal order creation failed');

        const { approval_url } = await response.json();
        window.location.href = approval_url; // Redirect to PayPal
      }
    } catch (error) {
      alert('Fehler bei der Zahlungsabwicklung. Bitte versuchen Sie es erneut.');
      console.error('Payment error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  */

  const handleSubmit = async (e) => {
    e.preventDefault();

    // For enterprise (Veranstalter), submit contact request at step 3
    if (formData.selectedPlan === 'enterprise' && currentStep === 3) {
      await submitEnterpriseRequest();
    } else if (currentStep === 5) {
      // Submit booking order via email
      await submitBookingOrder();
    }
    // Payment step (6) is disabled - see processPayment() below
    // } else if (currentStep === 6) {
    //   await processPayment();
    // }
  };

  const submitBookingOrder = async () => {
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8010';

      const response = await fetch(`${backendUrl}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'booking',
          form_data: {
            ...formData,
            total: calculateTotal(),
            planLabel: planOptions.find(p => p.value === formData.selectedPlan)?.label
          },
          timestamp: new Date().toISOString()
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Bestellung erfolgreich aufgegeben!', {
          description: 'Wir melden uns innerhalb von 24 Stunden bei Ihnen zur Zahlungsabwicklung.',
          duration: 5000,
          icon: <CheckCircle2 className="w-5 h-5" />
        });
        setIsOpen(false);
        setCurrentStep(1);
        // Reset form
        setFormData({
          selectedPlan: selectedPlan || '',
          addTimeTracking: false, addIDA: false,
          companyName: '', industry: '', employees: '', locations: '',
          firstName: '', lastName: '', email: '', phone: '', position: '',
          billingCompany: '', billingAddress: '', billingCity: '', billingZip: '',
          billingCountry: 'Deutschland', vatNumber: '',
          onboardingDate: '', onboardingTime: '', onboardingType: 'online',
          specialRequirements: '',
          paymentMethod: 'stripe', cardNumber: '', cardExpiry: '', cardCvc: '', cardName: '',
          acceptTerms: false, acceptPrivacy: false
        });
      } else if (result.blocked) {
        toast.error('Online-Bestellung nicht verfügbar', {
          description: result.contact_message || 'Bitte kontaktieren Sie uns telefonisch oder per E-Mail.',
          duration: 8000
        });
      } else {
        toast.error('Fehler beim Senden', {
          description: result.message || 'Bitte versuchen Sie es erneut.',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error('Verbindungsfehler', {
        description: 'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
        duration: 4000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitEnterpriseRequest = async () => {
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8010';

      const response = await fetch(`${backendUrl}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'booking',
          form_data: {
            ...formData,
            planType: 'Enterprise (Veranstalter)',
            message: `Individuelle Anfrage für Veranstalter - Position: ${formData.position || 'Nicht angegeben'}, Mitarbeiter: ${formData.employees || 'Nicht angegeben'}, Standorte: ${formData.locations || 'Nicht angegeben'}`
          },
          timestamp: new Date().toISOString()
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Anfrage erfolgreich gesendet!', {
          description: 'Unser Team wird sich schnellstmöglich mit einem individuellen Angebot bei Ihnen melden.',
          duration: 5000,
          icon: <CheckCircle2 className="w-5 h-5" />
        });
        setIsOpen(false);
        setCurrentStep(1);
        setFormData({
          selectedPlan: selectedPlan || '',
          addTimeTracking: false, addIDA: false,
          companyName: '', industry: '', employees: '', locations: '',
          firstName: '', lastName: '', email: '', phone: '', position: '',
          billingCompany: '', billingAddress: '', billingCity: '', billingZip: '',
          billingCountry: 'Deutschland', vatNumber: '',
          onboardingDate: '', onboardingTime: '', onboardingType: 'online',
          specialRequirements: '',
          paymentMethod: 'stripe', cardNumber: '', cardExpiry: '', cardCvc: '', cardName: '',
          acceptTerms: false, acceptPrivacy: false
        });
      } else if (result.blocked) {
        toast.error('Online-Anfrage nicht verfügbar', {
          description: result.contact_message || 'Bitte kontaktieren Sie uns telefonisch oder per E-Mail.',
          duration: 8000
        });
      } else {
        toast.error('Fehler beim Senden', {
          description: result.message || 'Bitte versuchen Sie es erneut.',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('Enterprise request error:', error);
      toast.error('Verbindungsfehler', {
        description: 'Bitte kontaktieren Sie uns direkt unter info@idaki.de oder +49 (0) 176 23 94 38 96.',
        duration: 6000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        // Special handling for enterprise (Veranstalter) plan
        if (formData.selectedPlan === 'enterprise') {
          return (
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-teal-500" />
                <h3 className="text-lg font-semibold">Individuelles Angebot für Veranstalter</h3>
              </div>
              
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <Building className="w-6 h-6 text-teal-600 mt-1" />
                  <div>
                    <h4 className="text-lg font-semibold text-teal-900 mb-2">
                      Für Veranstalter erstellen wir individuelle Angebote basierend auf Ihren spezifischen Anforderungen
                    </h4>
                    <p className="text-teal-700">
                      Unser Experten-Team entwickelt eine maßgeschneiderte Lösung für Ihre Veranstaltung. 
                      Kontaktieren Sie uns für ein persönliches Beratungsgespräch.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Ihre Vorteile:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Individuell angepasste Funktionen
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Skalierbare Lösungen für jede Veranstaltungsgröße
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Persönlicher Support vor Ort
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Flexible Vertragslaufzeiten
                  </li>
                </ul>
              </div>
            </div>
          );
        }

        // Standard plan selection for other plans
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-teal-500" />
              <h3 className="text-lg font-semibold">Abo-Auswahl</h3>
            </div>
            
            {/* Gewähltes Abo anzeigen */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Gewähltes Abo:</p>
                  <p className="font-bold text-lg text-teal-900">
                    {planOptions.find(p => p.value === formData.selectedPlan)?.label || 'Nicht gewählt'}
                  </p>
                  <p className="text-teal-700">
                    {planOptions.find(p => p.value === formData.selectedPlan)?.price}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-teal-600" />
              </div>
            </div>

            {/* Upgrade zu Jahresabo - nur bei Monatsabos */}
            {(formData.selectedPlan === 'ida-monthly' || formData.selectedPlan === 'timetracking-monthly') && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-orange-300 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">💰</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-orange-900 mb-2">Zu Jahresabo wechseln und 17% sparen!</h4>
                    <p className="text-sm text-orange-800 mb-3">
                      Sparen Sie 2 Monate bei Jahresbuchung
                    </p>
                    <Button
                      type="button"
                      onClick={() => {
                        const yearlyPlan = formData.selectedPlan === 'ida-monthly' ? 'ida-yearly' : 'timetracking-yearly';
                        handleSelectChange('selectedPlan', yearlyPlan);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Jetzt zum Jahresabo wechseln
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Zusätzliches Modul hinzufügen */}
            {(formData.selectedPlan.startsWith('ida-')) && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-3">Zeiterfassungsmodul hinzufügen (optional)</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 cursor-pointer">
                    <Checkbox
                      id="addTimeTrackingMonthly"
                      checked={formData.addTimeTracking === 'monthly'}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, addTimeTracking: checked ? 'monthly' : false }))}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label htmlFor="addTimeTrackingMonthly" className="font-medium cursor-pointer">
                        Zeiterfassung Monatsabo (+€39/Monat)
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">
                        Bis zu 10 Mitarbeiter, Zeiterfassung & Projektverfolgung
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 cursor-pointer">
                    <Checkbox
                      id="addTimeTrackingYearly"
                      checked={formData.addTimeTracking === 'yearly'}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, addTimeTracking: checked ? 'yearly' : false }))}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label htmlFor="addTimeTrackingYearly" className="font-medium cursor-pointer">
                        Zeiterfassung Jahresabo (+€390/Jahr) 
                        <span className="ml-2 text-green-600 font-semibold">2 Monate kostenlos!</span>
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">
                        Bis zu 10 Mitarbeiter, Zeiterfassung & Projektverfolgung
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(formData.selectedPlan.startsWith('timetracking-')) && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-3">IDA Compliance-Modul hinzufügen (optional)</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-3 bg-teal-50 rounded-lg border border-teal-200 hover:bg-teal-100 cursor-pointer">
                    <Checkbox
                      id="addIDAMonthly"
                      checked={formData.addIDA === 'monthly'}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, addIDA: checked ? 'monthly' : false }))}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label htmlFor="addIDAMonthly" className="font-medium cursor-pointer">
                        IDA Monatsabo (+€34/Monat)
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">
                        🎯 Spieler Selbsttest inklusive, Compliance-Management
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-teal-50 rounded-lg border border-teal-200 hover:bg-teal-100 cursor-pointer">
                    <Checkbox
                      id="addIDAYearly"
                      checked={formData.addIDA === 'yearly'}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, addIDA: checked ? 'yearly' : false }))}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label htmlFor="addIDAYearly" className="font-medium cursor-pointer">
                        IDA Jahresabo (+€340/Jahr)
                        <span className="ml-2 text-green-600 font-semibold">2 Monate kostenlos!</span>
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">
                        🎯 Spieler Selbsttest inklusive, Compliance-Management
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {formData.selectedPlan && formData.selectedPlan !== 'enterprise' && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-semibold">Gesamtbetrag (netto):</span>
                  <span className="text-xl font-bold text-teal-600">€{calculateTotal()}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <span>zzgl. 19% MwSt.:</span>
                  <span>€{(calculateTotal() * 0.19).toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-300 mt-2 pt-2 flex justify-between items-center">
                  <span className="font-bold">Gesamtbetrag (brutto):</span>
                  <span className="text-xl font-bold text-gray-900">€{(calculateTotal() * 1.19).toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Building className="w-5 h-5 text-teal-500" />
              <h3 className="text-lg font-semibold">Unternehmensdaten</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="companyName">Unternehmensname *</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="industry">Branche *</Label>
                <Select value={formData.industry} onValueChange={(value) => handleSelectChange('industry', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Wählen Sie Ihre Branche" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="employees">Anzahl Mitarbeiter</Label>
                <Select value={formData.employees} onValueChange={(value) => handleSelectChange('employees', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Mitarbeiteranzahl" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-5">1-5 Mitarbeiter</SelectItem>
                    <SelectItem value="6-20">6-20 Mitarbeiter</SelectItem>
                    <SelectItem value="21-50">21-50 Mitarbeiter</SelectItem>
                    <SelectItem value="51-100">51-100 Mitarbeiter</SelectItem>
                    <SelectItem value="100+">Über 100 Mitarbeiter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="locations">Anzahl Standorte</Label>
                <Input
                  id="locations"
                  name="locations"
                  type="number"
                  min="1"
                  value={formData.locations}
                  onChange={handleInputChange}
                  className="mt-1"
                  placeholder="z.B. 3"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-teal-500" />
              <h3 className="text-lg font-semibold">Kontaktdaten</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">Vorname *</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Nachname *</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">E-Mail-Adresse *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefonnummer *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="position">Position im Unternehmen</Label>
              <Input
                id="position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                className="mt-1"
                placeholder="z.B. Geschäftsführer, IT-Leiter"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-teal-500" />
              <h3 className="text-lg font-semibold">Rechnungsdaten</h3>
            </div>
            
            <div>
              <Label htmlFor="billingCompany">Rechnungsempfänger *</Label>
              <Input
                id="billingCompany"
                name="billingCompany"
                value={formData.billingCompany}
                onChange={handleInputChange}
                required
                className="mt-1"
                placeholder="Firmenname für Rechnung"
              />
            </div>

            <div>
              <Label htmlFor="billingAddress">Rechnungsadresse *</Label>
              <Input
                id="billingAddress"
                name="billingAddress"
                value={formData.billingAddress}
                onChange={handleInputChange}
                required
                className="mt-1"
                placeholder="Straße und Hausnummer"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="billingZip">PLZ *</Label>
                <Input
                  id="billingZip"
                  name="billingZip"
                  value={formData.billingZip}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="billingCity">Ort *</Label>
                <Input
                  id="billingCity"
                  name="billingCity"
                  value={formData.billingCity}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="billingCountry">Land *</Label>
                <Select value={formData.billingCountry} onValueChange={(value) => handleSelectChange('billingCountry', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Deutschland">Deutschland</SelectItem>
                    <SelectItem value="Österreich">Österreich</SelectItem>
                    <SelectItem value="Schweiz">Schweiz</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="vatNumber">USt-IdNr. (optional)</Label>
              <Input
                id="vatNumber"
                name="vatNumber"
                value={formData.vatNumber}
                onChange={handleInputChange}
                className="mt-1"
                placeholder="DE123456789"
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-teal-500" />
              <h3 className="text-lg font-semibold">Onboarding-Termin</h3>
            </div>
            
            <div className="bg-teal-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-teal-600 mt-1" />
                <div>
                  <h4 className="font-medium text-teal-900">Kostenloses Onboarding inklusive!</h4>
                  <p className="text-sm text-teal-700 mt-1">
                    Unser Experten-Team hilft Ihnen beim Setup und der Konfiguration von IDA KI.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="onboardingType">Onboarding-Art</Label>
                <Select value={formData.onboardingType} onValueChange={(value) => handleSelectChange('onboardingType', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online-Session</SelectItem>
                    <SelectItem value="vorort">Vor-Ort Termin</SelectItem>
                    <SelectItem value="telefonisch">Telefonisch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="onboardingDate">Wunschtermin</Label>
                <Input
                  id="onboardingDate"
                  name="onboardingDate"
                  type="date"
                  value={formData.onboardingDate}
                  onChange={handleInputChange}
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label htmlFor="onboardingTime">Wunschzeit</Label>
                <Select value={formData.onboardingTime} onValueChange={(value) => handleSelectChange('onboardingTime', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Zeitslot wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="specialRequirements">Besondere Anforderungen</Label>
              <Textarea
                id="specialRequirements"
                name="specialRequirements"
                value={formData.specialRequirements}
                onChange={handleInputChange}
                className="mt-1"
                placeholder="Gibt es spezielle Anforderungen für das Setup oder besondere Funktionen?"
                rows={3}
              />
            </div>

            {/* Rechtliche Zustimmung */}
            <div className="border-t pt-6 space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="acceptTerms"
                  checked={formData.acceptTerms}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, acceptTerms: checked }))}
                  className="mt-1"
                />
                <Label htmlFor="acceptTerms" className="text-sm leading-relaxed">
                  Ich akzeptiere die{' '}
                  <a href="/agb" target="_blank" className="text-teal-600 hover:text-teal-700 font-medium">AGB</a>
                  {' '}und stimme dem Abschluss des Abonnements zu.
                </Label>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="acceptPrivacy"
                  checked={formData.acceptPrivacy}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, acceptPrivacy: checked }))}
                  className="mt-1"
                />
                <Label htmlFor="acceptPrivacy" className="text-sm leading-relaxed">
                  Ich habe die{' '}
                  <a href="/datenschutz" target="_blank" className="text-teal-600 hover:text-teal-700 font-medium">Datenschutzerklärung</a>
                  {' '}gelesen und stimme der Verarbeitung meiner Daten zu.
                </Label>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">Gesamtbetrag:</span>
                <span className="text-2xl font-bold text-teal-600">€{calculateTotal()}</span>
              </div>
              <p className="text-sm text-gray-600">
                {formData.selectedPlan?.includes('yearly') ? 'Jährliche Abrechnung' : 'Monatliche Abrechnung'}
              </p>
            </div>
          </div>
        );

      /* PAYMENT STEP (6) DISABLED - Orders are sent via email instead
      case 6:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-teal-500" />
              <h3 className="text-lg font-semibold">Zahlungsmethode</h3>
            </div>

            <div className="space-y-4">
              <Label>Zahlungsmethode wählen</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  aria-label="Kreditkarte als Zahlungsmethode wählen"
                  aria-pressed={formData.paymentMethod === 'stripe'}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    formData.paymentMethod === 'stripe'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleSelectChange('paymentMethod', 'stripe')}
                >
                  <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-600" aria-hidden="true" />
                  <div className="font-semibold">Kreditkarte</div>
                  <div className="text-sm text-gray-500">Visa, Mastercard, American Express</div>
                </button>

                <button
                  type="button"
                  aria-label="PayPal als Zahlungsmethode wählen"
                  aria-pressed={formData.paymentMethod === 'paypal'}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    formData.paymentMethod === 'paypal'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleSelectChange('paymentMethod', 'paypal')}
                >
                  <div className="w-8 h-8 mx-auto mb-2 bg-blue-600 text-white rounded font-bold flex items-center justify-center" aria-hidden="true">
                    P
                  </div>
                  <div className="font-semibold">PayPal</div>
                  <div className="text-sm text-gray-500">Sicher mit PayPal bezahlen</div>
                </button>
              </div>
            </div>

            {formData.paymentMethod === 'stripe' && (
              <div className="border-t pt-6">
                <h4 className="text-md font-semibold mb-4">Kreditkarten-Details</h4>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="cardName">Name auf der Karte *</Label>
                    <Input
                      id="cardName"
                      name="cardName"
                      value={formData.cardName}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="Max Mustermann"
                    />
                  </div>

                  <div>
                    <Label htmlFor="cardNumber">Kartennummer *</Label>
                    <Input
                      id="cardNumber"
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="1234 5678 9012 3456"
                      maxLength="19"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cardExpiry">Gültig bis *</Label>
                      <Input
                        id="cardExpiry"
                        name="cardExpiry"
                        value={formData.cardExpiry}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        placeholder="MM/JJ"
                        maxLength="5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cardCvc">CVC *</Label>
                      <Input
                        id="cardCvc"
                        name="cardCvc"
                        value={formData.cardCvc}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        placeholder="123"
                        maxLength="4"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {formData.paymentMethod === 'paypal' && (
              <div className="border-t pt-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded font-bold flex items-center justify-center text-sm">
                      P
                    </div>
                    <div>
                      <h4 className="font-medium text-blue-900">PayPal Zahlung</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Sie werden zu PayPal weitergeleitet, um die Zahlung sicher abzuschließen.
                        Danach kehren Sie automatisch zurück.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="border-t pt-6">
              <h4 className="text-lg font-semibold mb-4">Bestellübersicht</h4>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span>Gewähltes Paket:</span>
                  <span className="font-medium">
                    {planOptions.find(p => p.value === formData.selectedPlan)?.label}
                  </span>
                </div>
                {formData.addTimeTracking && (
                  <div className="flex justify-between">
                    <span>Zeiterfassungsmodul:</span>
                    <span className="font-medium">
                      €{formData.selectedPlan === 'yearly' ? '390/Jahr' : '39/Monat'}
                    </span>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between">
                  <span className="text-lg font-semibold">Gesamtbetrag:</span>
                  <span className="text-2xl font-bold text-teal-600">€{calculateTotal()}</span>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                <div>
                  <h4 className="text-sm font-semibold text-green-800">Sicher & verschlüsselt</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Alle Zahlungen werden über SSL-verschlüsselte Verbindungen abgewickelt.
                    Ihre Daten sind bei uns sicher.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      */

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            {formData.selectedPlan === 'enterprise' ? 'Individuelles Angebot anfragen' : 'IDA KI Abo abschließen'}
          </DialogTitle>
          <DialogDescription>
            {formData.selectedPlan === 'enterprise'
              ? `Schritt ${currentStep} von 3: ${['Individuelles Angebot', 'Unternehmensdaten', 'Kontaktdaten'][currentStep - 1]}`
              : `Schritt ${currentStep} von 5: ${['Abo-Auswahl', 'Unternehmensdaten', 'Kontaktdaten', 'Rechnungsdaten', 'Onboarding'][currentStep - 1]}`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div
            className="bg-teal-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / (formData.selectedPlan === 'enterprise' ? 3 : 5)) * 100}%` }}
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {renderStep()}

          {/* Navigation Buttons */}
          <div className="border-t pt-6 flex justify-between">
            <div>
              {currentStep > 1 && (
                <Button type="button" variant="outline" onClick={prevStep}>
                  Zurück
                </Button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsOpen(false)}
              >
                Abbrechen
              </Button>
              
              {/* Enterprise (Veranstalter) flow */}
              {formData.selectedPlan === 'enterprise' ? (
                currentStep < 3 ? (
                  <Button 
                    type="button" 
                    onClick={nextStep}
                    className="bg-teal-500 hover:bg-teal-600"
                    disabled={currentStep === 1 && !formData.selectedPlan}
                  >
                    Weiter
                  </Button>
                ) : (
                  <Button 
                    type="submit" 
                    className="bg-teal-500 hover:bg-teal-600"
                    disabled={isSubmitting || !formData.firstName || !formData.lastName || !formData.email}
                  >
                    {isSubmitting ? 'Sende Anfrage...' : 'Individuelles Angebot anfragen'}
                  </Button>
                )
              ) : (
                /* Standard subscription flow */
                currentStep < 5 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="bg-teal-500 hover:bg-teal-600"
                    disabled={currentStep === 1 && !formData.selectedPlan}
                  >
                    Weiter
                  </Button>
                ) : currentStep === 5 ? (
                  <Button
                    type="submit"
                    className="bg-teal-500 hover:bg-teal-600"
                    disabled={isSubmitting || !formData.acceptTerms || !formData.acceptPrivacy}
                  >
                    {isSubmitting ? 'Sende Bestellung...' : 'Bestellung absenden'}
                  </Button>
                ) : null
                /* Payment buttons removed - see case 6 above
                : (
                  <Button
                    type="submit"
                    className={`${
                      formData.paymentMethod === 'paypal'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-teal-500 hover:bg-teal-600'
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? 'Verarbeite Zahlung...'
                      : formData.paymentMethod === 'paypal'
                        ? 'Mit PayPal bezahlen'
                        : 'Mit Kreditkarte bezahlen'
                    }
                  </Button>
                )
                */
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};